import { Button, TypographyProps } from "@mui/material";
import { useCallback } from "react";

import PageTabsV2 from "@/App/Layouts/PageBody/PageTabsV2";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppLink from "@/common/components/Link/AppLink";
import { AppModalCloseIconButton } from "@/common/components/Modals/AppModalCloseIconButton";
import { PersistenceKey } from "@/common/constants/persistenceKey";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useCommonViewParams } from "@/common/hooks/layout/useCommonViewParams";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ViewVariant } from "@/common/ts/layout";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  DataUpdatesHubClientMethodName,
  EntityType,
  RepairOperationDto,
  RepairOperationStage,
  VehicleOperationType,
} from "@/core/api/generated/v1";

import { VehicleOperationViewPageHeader } from "../../Vehicle/View/Header/VehicleOperationViewPageHeader";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import RepairOperationMenu from "../RepairOperationMenu";
import CommentsTabContent from "./Tabs/CommentsTabContent";
import ContractTabContent from "./Tabs/ContractTabContent";
import GeneralHistoryTabContent from "./Tabs/GeneralHistoryTabContent";
import InboxTabContent from "./Tabs/InboxTabContent";
import OverviewTabContent from "./Tabs/OverviewTabContent";
import RelatedOperationsTabContent from "./Tabs/RelatedOperationsTabContent";
import VehicleTabContent from "./Tabs/VehicleTabContent";

export type RepairOperationViewPageHighlightProps = {
  /** Item to highlight when mounted. */
  itemId?: string | null;
};

export enum RepairOperationViewPageTabs {
  Overview = "Overview",
  Inbox = "Inbox",
  Vehicle = "Vehicle",
  Contract = "Contract",
  RelatedOperations = "RelatedOperations",
  GeneralHistory = "GeneralHistory",
  Comments = "Comments",
}

const defaultHeaderProps = {
  withLink: false,
  typographyProps: undefined as Partial<TypographyProps> | undefined,
};

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};

interface OwnProps extends BaseEntityViewInheritableProps<RepairOperationDto> {
  repairOperationId: string | null | undefined;
  repairOperation: RepairOperationDto | null | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  headerProps?: Partial<typeof defaultHeaderProps>;
}

export type RepairOperationViewProps = OwnProps;

export default function RepairOperationView({
  repairOperationId,
  repairOperation,
  displayProps = defaultDisplayProps,
  headerProps = defaultHeaderProps,
  onUpdate,
  onDelete,
  onClose,
}: RepairOperationViewProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };
  headerProps = {
    ...defaultHeaderProps,
    ...headerProps,
  };

  const history = useAppHistory();
  const { enqueueSnackbar } = useAppSnackbar();
  const currentTenant = useCurrentTenant();
  const commonViewParams = useCommonViewParams({
    statePersistence: {
      persistenceKey: PersistenceKey.forEntityView(EntityType.RepairOperation),
      viewVariant: displayProps.viewVariant,
    },
  });

  const repairOperationRequest = useApiRequest(
    apiClient.repairOperationsApi.apiV1RepairOperationsRepairOperationIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      repairOperationId: repairOperationId!,
    },
    {
      deps: [repairOperationId],
      skip: !repairOperationId || !!repairOperation,
    },
  );
  repairOperation = repairOperation || repairOperationRequest?.data;

  useRealtimeDataUpdates({
    channelNames: [
      DataUpdatesChannelName.Entity(
        currentTenant?.id,
        EntityType.RepairOperation,
        repairOperation?.id || "",
      ),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      repairOperationRequest.handleEntityChanged(data);
    },
  });

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb:
      (displayProps?.breadcrumbs &&
        repairOperation && {
          idValue: repairOperation.id!,
          newTitle: repairOperation.localNumber || "",
        }) ||
      undefined,
  });

  const handleUpdateStage = useCallback(
    async (newStage: RepairOperationStage) => {
      try {
        const response =
          await apiClient.repairOperationsApi.apiV1RepairOperationsRepairOperationIdStagePut({
            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            repairOperationId: repairOperationId!,
            generalStageUpdateDtoOfRepairOperationStage: {
              newStage,
            },
          });
        repairOperationRequest.replaceData(response.data);
        onUpdate?.(response.data);
      } catch (err) {
        const validation2 = ValidationHelper.handleApiErrorResponse(err);
        enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
        throw err;
      }
    },
    [repairOperationId],
  );

  return (
    <BaseEntityView
      entityType={EntityType.RepairOperation}
      entityId={repairOperationId}
      entity={repairOperation}
      entityRequest={repairOperationRequest}
    >
      <ViewLayout
        displayProps={displayProps}
        header={
          displayProps.header &&
          repairOperation && (
            <VehicleOperationViewPageHeader
              operationType={VehicleOperationType.RepairOperation}
              operationEntity={repairOperation}
              vehicleId={repairOperation?.vehicle?.id}
              vehicle={repairOperation?.vehicle || undefined}
              titleProps={{
                to: headerProps?.withLink
                  ? ROUTE_PATH.REPAIR_OPERATION_VIEW(repairOperation?.id)
                  : undefined,
                typographyProps: headerProps?.typographyProps,
              }}
              primaryActions={
                displayProps.actions && (
                  <AuthorizedElement permissions={[AppPermission.RepairOperationManage]}>
                    <Button
                      component={AppLink}
                      to={ROUTE_PATH.REPAIR_OPERATION_EDIT(repairOperation?.id)}
                      variant='outlined'
                      color='text'
                      size='medium'
                      startIcon={<AppIcon of='edit' />}
                    >
                      Edit
                    </Button>
                  </AuthorizedElement>
                )
              }
              secondaryActions={
                displayProps.actions &&
                repairOperation && (
                  <RepairOperationMenu
                    entity={repairOperation}
                    displayProps={{
                      actions: {
                        edit: false,
                        delete: true,
                        addToAssignment: true,
                      },
                    }}
                    onUpdate={(newValue) => {
                      newValue
                        ? repairOperationRequest.replaceData(newValue)
                        : repairOperationRequest.refetch();
                      onUpdate?.(newValue);
                    }}
                    onDelete={() => (onDelete ? onDelete() : history.goBack())}
                  />
                )
              }
              closeAction={
                displayProps.closeInHeader && onClose ? (
                  <AppModalCloseIconButton onClick={onClose} />
                ) : undefined
              }
              onTagsUpdated={(newValue) => {
                repairOperationRequest.updateData((x) => {
                  x.tags = newValue || undefined;
                  onUpdate?.(x);
                });
              }}
            />
          )
        }
      >
        <PageTabsV2
          tabIdsDefinition={RepairOperationViewPageTabs}
          defaultTabId={RepairOperationViewPageTabs.Overview}
          commonViewParams={commonViewParams}
          tabs={[
            { label: "Overview", value: RepairOperationViewPageTabs.Overview, isHideable: false },
            { label: "Chats", value: RepairOperationViewPageTabs.Inbox },
            {
              label: "Vehicle",
              value: RepairOperationViewPageTabs.Vehicle,
              permissions: [AppPermission.VehicleRead],
            },
            ...(repairOperation?.contract?.id
              ? [
                  {
                    label: "Contract",
                    value: RepairOperationViewPageTabs.Contract,
                    permissions: [AppPermission.ContractRead],
                  },
                ]
              : []),
            {
              label: "Related operations",
              value: RepairOperationViewPageTabs.RelatedOperations,
            },
            {
              label: "History",
              value: RepairOperationViewPageTabs.GeneralHistory,
              permissions: [AppPermission.GeneralHistoryRead],
            },
            { label: "Comments", value: RepairOperationViewPageTabs.Comments },
          ]}
        >
          {({ activeTabId: activeTab }) =>
            repairOperation && (
              <>
                {activeTab === RepairOperationViewPageTabs.Overview && (
                  <OverviewTabContent
                    handleUpdateStage={handleUpdateStage}
                    repairOperation={repairOperation}
                    onUpdate={(newValue) => {
                      newValue
                        ? repairOperationRequest.replaceData(newValue)
                        : repairOperationRequest.refetch();
                      onUpdate?.(newValue);
                    }}
                  />
                )}
                {activeTab === RepairOperationViewPageTabs.Inbox && (
                  <InboxTabContent repairOperation={repairOperation} />
                )}
                {activeTab === RepairOperationViewPageTabs.Vehicle && (
                  <VehicleTabContent repairOperation={repairOperation} />
                )}
                {activeTab === RepairOperationViewPageTabs.Contract && (
                  <ContractTabContent repairOperation={repairOperation} />
                )}
                {activeTab === RepairOperationViewPageTabs.RelatedOperations && (
                  <RelatedOperationsTabContent repairOperation={repairOperation} />
                )}
                {activeTab === RepairOperationViewPageTabs.GeneralHistory && (
                  <GeneralHistoryTabContent repairOperation={repairOperation} />
                )}
                {activeTab === RepairOperationViewPageTabs.Comments && (
                  <CommentsTabContent repairOperation={repairOperation} />
                )}
              </>
            )
          }
        </PageTabsV2>
      </ViewLayout>
    </BaseEntityView>
  );
}
